import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Spinner from 'components/Spinner/Spinner';
import {
  getSignedUrlCached,
  invalidateSignedUrl,
} from 'scenes/ProjectGallery/utils/signedUrlCache';
import { PAGE_GUTTER } from './useMobilePhotoGestures';
import MobileVideoPane from './MobileVideoPane';

// Mobile-only 3-pane pager: prev/current/next rendered side by side so the
// neighbor tracks the finger during a swipe (iPhone Photos style). The strip
// and the center pane's zoom layer are driven imperatively by
// useMobilePhotoGestures via stripRef/zoomLayerRef — neither element declares
// a transform here, so React never clobbers the direct style writes.
// Panes are keyed by filePermission.id: when a swipe commits, the neighbor
// pane keeps its DOM node (and decoded image) as it becomes the center.

const useStyles = makeStyles(() => ({
  strip: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    willChange: 'transform',
  },
  pane: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  zoomLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transformOrigin: '0 0',
  },
  media: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
}));

function MobileImageViewer({
  filePermission,
  prevFilePermission,
  nextFilePermission,
  rotation,
  isZoomed,
  stripRef,
  zoomLayerRef,
  imgRef,
  onImgLoad,
  videoApiRef,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [srcById, setSrcById] = useState({});
  const retriedAtRef = useRef(new Map()); // fp.id -> last retry timestamp

  const prevId = prevFilePermission && prevFilePermission.id;
  const nextId = nextFilePermission && nextFilePermission.id;

  useEffect(() => {
    let cancelled = false;
    [prevFilePermission, filePermission, nextFilePermission]
      .filter(Boolean)
      .forEach((fp) => {
        const isVideoFile = fp.file.mimeType.includes('video');
        getSignedUrlCached(fp.id)
          .then((url) => {
            if (cancelled) return;
            const resolved = url || theme.component.imagePlaceholder;
            if (!isVideoFile && fp.id !== filePermission.id && url) {
              new Image().src = url; // warm the neighbor's bytes
            }
            setSrcById((prev) =>
              prev[fp.id] === resolved ? prev : { ...prev, [fp.id]: resolved }
            );
          })
          .catch(() => {});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevId, filePermission.id, nextId]);

  // A prefetched neighbor promoted to center keeps its DOM node (same key),
  // so its load event never re-fires — synthesize the callback for the
  // parent's `dimensions` state. Deferred a tick because the parent's
  // [filePermission] effect resets dimensions AFTER this child effect runs.
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !img.complete || !img.naturalWidth) return undefined;
    const timer = setTimeout(() => onImgLoad({ target: img }), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePermission.id]);

  // A pane's signed URL can expire mid-session — invalidate and refetch.
  // Videos live for the whole dialog session and keep issuing range
  // requests, so expiry can legitimately strike the same file repeatedly:
  // the guard is time-based (blocks tight error loops on a broken file,
  // allows a fresh retry after a later expiry) and the dead URL is evicted
  // from the module cache even inside the guard window.
  const handleImgError = (fp) => () => {
    invalidateSignedUrl(fp.id);
    const lastRetryAt = retriedAtRef.current.get(fp.id) || 0;
    if (Date.now() - lastRetryAt < 60 * 1000) return;
    retriedAtRef.current.set(fp.id, Date.now());
    setSrcById((prev) => {
      const next = { ...prev };
      delete next[fp.id];
      return next;
    });
    getSignedUrlCached(fp.id)
      .then((url) => {
        if (url) setSrcById((prev) => ({ ...prev, [fp.id]: url }));
      })
      .catch(() => {});
  };

  const panes = [
    { fp: prevFilePermission, offset: -1 },
    { fp: filePermission, offset: 0 },
    { fp: nextFilePermission, offset: 1 },
  ].filter((pane) => pane.fp);

  const renderPaneContent = (fp, isCenter) => {
    const src = srcById[fp.id];
    const isVideoFile = fp.file.mimeType.includes('video');
    const isImageFile = fp.file.mimeType.includes('image');

    if (isImageFile) {
      const paneRotation = isCenter ? rotation : fp.rotation || 0;
      return (
        <div
          className={classes.zoomLayer}
          ref={isCenter ? zoomLayerRef : undefined}
        >
          {src ? (
            <img
              className={classes.media}
              ref={isCenter ? imgRef : undefined}
              alt={fp.name}
              src={src}
              style={{
                transform: `rotate(${paneRotation}deg)`,
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                pointerEvents: 'none',
              }}
              onLoad={isCenter ? onImgLoad : undefined}
              onError={handleImgError(fp)}
              draggable={false}
            />
          ) : isCenter ? (
            <Spinner />
          ) : null}
        </div>
      );
    }

    if (isVideoFile) {
      return (
        <MobileVideoPane
          filePermission={fp}
          isCenter={isCenter}
          src={src}
          videoApiRef={isCenter ? videoApiRef : undefined}
          onSourceError={handleImgError(fp)}
        />
      );
    }

    return null;
  };

  return (
    <div className={classes.strip} ref={stripRef}>
      {panes.map(({ fp, offset }) => (
        <div
          key={fp.id}
          className={classes.pane}
          style={{
            transform:
              offset === 0
                ? 'none'
                : offset < 0
                  ? `translateX(calc(-100% - ${PAGE_GUTTER}px))`
                  : `translateX(calc(100% + ${PAGE_GUTTER}px))`,
            visibility: offset !== 0 && isZoomed ? 'hidden' : 'visible',
          }}
        >
          {renderPaneContent(fp, offset === 0)}
        </div>
      ))}
    </div>
  );
}

export default MobileImageViewer;
